import {
  Carousel,
  CarouselIndicator,
  CarouselMainContainer,
  CarouselNextLarge,
  CarouselPreviousLarge,
  CarouselThumbsContainer,
  SliderMainItem,
  SliderThumbItem,
} from "@/components/ui/carousel-extension";

import { isSafari } from "react-device-detect";

import { PhotoResponse, PhotoType, PrivatePhotoStatus } from "@/generated-api";
import ImageWithSpinner from "../ImageWithSpinner";
import { Check, EyeIcon, ImageIcon, LockIcon } from "lucide-react";
import { Button } from "../ui/button";
import { useRequestPrivatePhotos } from "@/tanstack-hooks";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { ZoomableImage } from "./ZoomableImage";

export default function PhotosCarousel({
  userPhotos,
  userId,
  privatePhotoStatus,
}: {
  userPhotos: PhotoResponse[] | undefined;
  userId: string;
  privatePhotoStatus: PrivatePhotoStatus;
}) {
  const { toast } = useToast();
  const requestPhotosMutation = useRequestPrivatePhotos();
  const [localPrivatePhotoStatus, setLocalPrivatePhotoStatus] =
    useState(privatePhotoStatus);
  const t = useTranslation();

  const onRequestPrivatePhotos = () => {
    setLocalPrivatePhotoStatus(PrivatePhotoStatus.REQUESTED);

    requestPhotosMutation.mutate(userId, {
      onError: (error) => {
        setLocalPrivatePhotoStatus(PrivatePhotoStatus.HIDDEN);
        console.error("Error requesting photos", error);
        toast({
          variant: "destructive",
          title: t.PHOTO_SHARING.ERRORS.REQUESTING,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  };

  return (
    <div className="-mx-2 md:mx-0 md:w-full overflow-hidden">
      <Carousel carouselOptions={{ duration: 14 }}>
        <div className="relative">
          <CarouselNextLarge />
          <CarouselPreviousLarge />
          <CarouselMainContainer
            className="w-full"
            style={{ height: "calc(4/3 * 100%)" }}
          >
            {userPhotos ? (
              <>
                {userPhotos.map((image) => (
                  <SliderMainItem
                    key={image.display_order}
                    className="w-full bg-black md:rounded-lg"
                    style={{ height: "calc(4/3 * 100%)" }}
                  >
                    <ZoomableImage image={image} />
                  </SliderMainItem>
                ))}
                {(privatePhotoStatus === PrivatePhotoStatus.HIDDEN ||
                  privatePhotoStatus === PrivatePhotoStatus.REQUESTED) && (
                  <SliderMainItem className="bg-black rounded-lg w-full">
                    <div className="w-full min-h-[100%] rounded-lg flex flex-col border-2 border-zinc-800 text-center items-center justify-center text-gray-400">
                      <LockIcon
                        className="mx-auto h-10 w-10 mb-4"
                        strokeWidth={1}
                      />
                      <span className="mb-6">
                        {t.PHOTO_SHARING.LABELS.HAS_PRIVATE_PHOTOS}
                      </span>
                      {localPrivatePhotoStatus ===
                      PrivatePhotoStatus.REQUESTED ? (
                        <Button>
                          <Check className="h-5 w-5 mr-2" />
                          {t.PHOTO_SHARING.LABELS.REQUESTED_BUTTON}
                        </Button>
                      ) : (
                        <Button onClick={onRequestPrivatePhotos}>
                          <EyeIcon className="h-5 w-5 mr-2" />
                          {t.PHOTO_SHARING.LABELS.REQUEST_BUTTON}
                        </Button>
                      )}
                    </div>
                  </SliderMainItem>
                )}
              </>
            ) : (
              <SliderMainItem className="aspect-[3/4] flex items-center justify-center bg-black rounded-lg">
                <div className="text-center text-gray-400">
                  <ImageIcon
                    className="mx-auto h-12 w-12 mb-2"
                    strokeWidth={1}
                  />
                  <p>{t.PHOTO_SHARING.LABELS.NO_IMAGES_AVAILABLE}</p>
                </div>
              </SliderMainItem>
            )}
          </CarouselMainContainer>

          {userPhotos && (
            <div
              className={`absolute left-1/2 -translate-x-1/2 ${
                isSafari ? "bottom-6" : "bottom-4"
              }`}
            >
              <CarouselThumbsContainer className="gap-x-1">
                {userPhotos.map((image) => (
                  <CarouselIndicator
                    key={image.display_order}
                    index={image.display_order}
                  />
                ))}
                {(privatePhotoStatus === PrivatePhotoStatus.HIDDEN ||
                  privatePhotoStatus == PrivatePhotoStatus.REQUESTED) && (
                  <CarouselIndicator
                    key={userPhotos.length}
                    index={userPhotos.length}
                  />
                )}
              </CarouselThumbsContainer>
            </div>
          )}
        </div>
        {userPhotos && (
          <CarouselThumbsContainer className="hidden md:flex">
            {userPhotos.map((image) => (
              <SliderThumbItem
                key={image.display_order}
                index={image.display_order}
                className="bg-transparent aspect-[3/4] max-w-20 hover:cursor-pointer"
                isPrivate={image.photo_type === PhotoType.PRIVATE}
              >
                <ImageWithSpinner
                  src={image.photo_url}
                  className="rounded-lg"
                  isThumbnail={true}
                />
              </SliderThumbItem>
            ))}
            {(privatePhotoStatus === PrivatePhotoStatus.HIDDEN ||
              privatePhotoStatus == PrivatePhotoStatus.REQUESTED) && (
              <SliderThumbItem
                key={userPhotos.length}
                index={userPhotos.length}
                className="bg-transparent aspect-[3/4] max-w-20 hover:cursor-pointer"
              >
                <div className="flex items-center justify-center aspect-[3/4] max-w-20 border-2 border-zinc-800 rounded-lg">
                  <LockIcon />
                </div>
              </SliderThumbItem>
            )}
          </CarouselThumbsContainer>
        )}
      </Carousel>
    </div>
  );
}
